import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormBlock from "../../../Components/Inputs/FormBlock";
import { Spinner } from "../../../Components/LoadingAnimations/LoadingAnimations";
import instance from "../../../Interceptor/api_instance";
import SupplierSearch from "../../../Components/Inputs/SupplierSearch";

const EmptyRow = () => ({ description: "", sku: "", cost: "", quantity: "1" });

const NewInvoice = ({ close, onCreate, maintenance_report_id }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    invoice_number: "",
    invoiced_at: new Date().toISOString().split("T")[0],
    purchase_order: "",
    selected_supplier: null,
    items: [EmptyRow()],
    files: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const updateItem = (index, key, value) => {
    setForm((p) => {
      const items = [...p.items];
      items[index] = { ...items[index], [key]: value };
      return { ...p, items };
    });
  };

  const addRow = () =>
    setForm((p) => ({ ...p, items: [...p.items, EmptyRow()] }));
  const removeRow = (index) =>
    setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== index) }));

  const onFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm((p) => ({ ...p, files: [...p.files, ...files] }));
    // Reset input so selecting the same file again will work
    e.target.value = null;
  };

  const removeFile = (index) =>
    setForm((p) => ({ ...p, files: p.files.filter((_, i) => i !== index) }));

  const validate = () => {
    if (!form.invoice_number || !form.invoice_number.trim()) {
      setError("Invoice number is required");
      return false;
    }
    if (!form.purchase_order) {
      setError("Purchase order is required");
      return false;
    }
    if (!form.items.length) {
      setError("At least one item is required");
      return false;
    }
    for (const row of form.items) {
      if (!row.description || !row.description.trim()) {
        setError("Each item must have a description");
        return false;
      }
      if (
        row.quantity == null ||
        isNaN(Number(row.quantity)) ||
        Number(row.quantity) < 1
      ) {
        setError("Each item must have a quantity of 1 or more");
        return false;
      }
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("maintenance_report_id", maintenance_report_id);
      fd.append("invoice_number", form.invoice_number);
      fd.append("purchase_order", form.purchase_order || "");
      fd.append("notes", form.notes || "");
      fd.append("invoiced_at", form.invoiced_at);
      fd.append("supplier", form.selected_supplier?.value);
      fd.append(
        "items",
        JSON.stringify(
          form.items.map((it) => ({
            description: it.description,
            sku: it.sku || null,
            cost: Number(it.cost),
            quantity: Number(it.quantity),
          })),
        ),
      );

      form.files.forEach((f) => fd.append("files", f));

      // Send as multipart/form-data so files are included. The global axios
      // instance sets Content-Type: application/json by default, so provide a
      // per-request header to ensure a proper multipart request (allows the
      // browser/axios to set the boundary).
      const res = await instance.post("safety/create-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 201) {
        onCreate && onCreate(res.data);
        close();
      } else {
        setError("Unexpected response from server");
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-mask page-mask-animation">
      <div className="edit-modal white-cont">
        <h2 className="global-h2">{t("Add Invoice")}</h2>
        {error && <p className="error">{error}</p>}
        <form onSubmit={handleSubmit}>
          <FormBlock
            label={t("Invoice Number")}
            name="invoice_number"
            value={form.invoice_number}
            update={updateField}
            isRequired={true}
            type="text"
          />
          <FormBlock
            label={t("Purchase Order")}
            name="purchase_order"
            value={form.purchase_order}
            update={updateField}
            isRequired={true}
            type="text"
            placeholder={t("Enter PO number or reference")}
          />
          <div className="form-block">
            <label className="required">{t("Invoiced At")}</label>
            <div className="input-cont">
              <input
                className="text-input"
                type="date"
                name="invoiced_at"
                value={form.invoiced_at}
                onChange={updateField}
              />
            </div>
          </div>
          <div className="form-block">
            <label className="required">{t("Supplier")}</label>
            <div className="input-cont">
              <SupplierSearch
                onSelect={(option) =>
                  setForm((prev) => ({
                    ...prev,
                    selected_supplier: option || null,
                  }))
                }
                selectedOption={form.selected_supplier}
              />
            </div>
          </div>
          <h4 className="global-h4">{t("Items Purchased")}</h4>
          {form.items.map((row, idx) => (
            <div key={idx} className="form-row">
              <FormBlock
                label={t("Description")}
                name={`description-${idx}`}
                value={row.description}
                update={(e) => updateItem(idx, "description", e.target.value)}
                isRequired={true}
                type="text"
              />
              <FormBlock
                label={t("SKU (optional)")}
                name={`sku-${idx}`}
                value={row.sku}
                update={(e) => updateItem(idx, "sku", e.target.value)}
                isRequired={false}
                type="text"
              />
              <div className="form-block">
                <label className="input-label required">{t("Cost")}</label>
                <div className="input-cont">
                  <input
                    className="text-input with-border"
                    type="number"
                    name={`cost-${idx}`}
                    value={row.cost}
                    onChange={(e) => updateItem(idx, "cost", e.target.value)}
                    required
                    step={"0.01"}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <FormBlock
                label={t("Quantity")}
                name={`quantity-${idx}`}
                value={row.quantity}
                update={(e) => updateItem(idx, "quantity", e.target.value)}
                isRequired={true}
                type="number"
                step={"1"}
                placeholder="1"
                min={1}
              />
              <div
                className="margin-bottom-24"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => removeRow(idx)}
                  disabled={form.items.length === 1}
                >
                  {t("buttons.remove")}
                </button>
              </div>
            </div>
          ))}
          <div className="form-row">
            <button type="button" className="change-pwd-btn" onClick={addRow}>
              {t("Add Item")}
            </button>
          </div>

          <h4 className="global-h4">{t("Attachments")}</h4>
          <div className="form-row">
            <input
              type="file"
              accept="application/pdf,image/*"
              capture
              multiple
              name="files"
              onChange={onFilesChange}
            />
          </div>
          {form.files.length > 0 && (
            <div className="file-previews">
              {form.files.map((f, i) => (
                <div key={i} className="file-preview">
                  {f.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(f)}
                      alt={f.name}
                      style={{ width: 80, height: 80, objectFit: "cover" }}
                    />
                  ) : (
                    <div className="file-icon">PDF</div>
                  )}
                  <div>{f.name}</div>
                  <button
                    type="button"
                    className="cancel-btn margin-bottom-24"
                    onClick={() => removeFile(i)}
                  >
                    {t("buttons.remove")}
                  </button>
                </div>
              ))}
            </div>
          )}

          {isSubmitting ? (
            <Spinner />
          ) : (
            <div className="full-cont">
              <button
                className="change-pwd-btn all-btn"
                type="submit"
                disabled={isSubmitting}
              >
                {t("buttons.submit")}
              </button>
              <button
                className="cancel-btn all-btn"
                type="button"
                onClick={close}
              >
                {t("buttons.cancel")}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default NewInvoice;
